/**
 * El menú de chats del encabezado: el ícono al lado de las notificaciones que despliega los
 * últimos chats, como el de Messenger.
 *
 * POR QUÉ. Hasta ahora, ver si un cliente contestó exigía ir a Chats — dejando lo que estabas
 * haciendo. Y no había ninguna señal en el encabezado: los mensajes sin leer eran invisibles desde
 * cualquier otra pantalla. El ícono con su contador resuelve lo segundo; la lista resuelve lo
 * primero, porque desde acá el chat se abre acoplado abajo y no te mueve de donde estabas.
 *
 * 🪤 NO HACE NINGUNA CONSULTA PROPIA. Las conversaciones ya viven en [WaInboxNavContext], que está
 * montado en todo el admin justamente para eso. Agregar acá un fetch más sería pagar dos veces por
 * la misma lista y —peor— tener dos versiones de la verdad: la del encabezado diría "3 sin leer" y
 * la bandeja diría otra cosa.
 *
 * 🪤 EL BUSCADOR FILTRA LO QUE YA ESTÁ CARGADO, NO LA BASE. Es un menú rápido, no el buscador de la
 * bandeja —que además busca dentro de los mensajes—. Por eso, cuando el filtro no encuentra nada,
 * no dice "sin resultados" y te deja ahí: te manda a buscar en Chats, que sí sabe hacerlo.
 */

import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ActionList, Icon, Popover, Text, TextField } from '@/lib/polaris';
import { ChatIcon, ChevronDownIcon, SearchIcon } from '@/lib/polaris-icons';
import { useWaInboxNavOptional } from '@/contexts/WaInboxNavContext';
import { useAuth } from '@smarty/shared';
import { useAdminToastOptional } from '@/contexts/AdminToastContext';
import {
  menuDeConversacion, persistirAccion, confirmacionNecesaria,
  type AccionDeConversacion,
} from '@/lib/whatsapp/accionesDeConversacion';
import { useWaChatDock } from '@/contexts/WaChatDockContext';
import { WaAvatar } from '@/components/whatsapp/WaAvatar';
import { PanelDelEncabezado, FilaDelPanel, Z_SOBRE_EL_PANEL } from '@/components/layout/PanelDelEncabezado';
import { ErrorBoundarySection } from '@/components/ErrorBoundarySection';
import { getAvatarColor, getInitials, waDisplayName, waMediaPreview, waTimeAgo } from '@/components/whatsapp/wa-helpers';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { conversationSlug } from '@/lib/conversationSlug';
import { WaDeliveryTicks } from '@/components/whatsapp/WaDeliveryTicks';
import type { WaConversation } from '@/hooks/useWaConversations';

/**
 * Cuántos chats entran en el menú. Sale de lo que hace falta para llenar el panel largo y poder
 * scrollear un poco: cortar en 12 dejaba la mitad de abajo vacía. Más que esto ya es la bandeja,
 * no un vistazo rápido.
 */
const MAX_EN_MENU = 30;

export function WaChatsMenu() {
  const [abierto, setAbierto] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [pestania, setPestania] = useState<'todos' | 'sin_leer' | 'grupos'>('todos');
  const storeNavigate = useStoreNavigate();
  /*
   * 🪤 VERSIÓN OPCIONAL DEL CONTEXTO A PROPÓSITO. El encabezado también se monta en Ajustes y en la
   * pantalla de carga, que tienen su propio Frame sin el provider de chats. Con la versión estricta,
   * este menú tumbaría esas dos pantallas enteras por una lista que ahí no hace falta.
   */
  const nav = useWaInboxNavOptional();
  const conversaciones = useMemo(() => nav?.nonArchivedConversations ?? [], [nav]);
  const dock = useWaChatDock();

  // Sin provider no hay chats que mostrar y tampoco dónde acoplarlos: el ícono no aparece.
  const sinContexto = nav === null;

  const sinLeer = useMemo(
    () => conversaciones.reduce((n, c) => n + (c.unread_count > 0 ? 1 : 0), 0),
    [conversaciones],
  );

  const grupos = useMemo(
    // Un grupo se reconoce por su identificador: WhatsApp no le da teléfono, le da "g:...".
    () => conversaciones.filter((c) => (c.contact_phone ?? '').startsWith('g:')).length,
    [conversaciones],
  );

  const visibles = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    let lista = conversaciones;
    if (pestania === 'sin_leer') lista = lista.filter((c) => c.unread_count > 0);
    if (pestania === 'grupos') lista = lista.filter((c) => (c.contact_phone ?? '').startsWith('g:'));
    if (q) {
      lista = lista.filter((c) =>
        (c.contact_name ?? '').toLowerCase().includes(q) || (c.contact_phone ?? '').includes(q));
    }
    return lista.slice(0, MAX_EN_MENU);
  }, [conversaciones, filtro, pestania]);

  /*
   * ¿Estamos DENTRO del módulo de Chats? La respuesta cambia qué significa "abrir un chat".
   *
   * 🪤 ESTANDO EN CHATS, EL DOCK NO SERVÍA — Y NO ES QUE SE VIERA MAL: NO PASABA NADA. Las ventanitas
   * se montan en el marco del admin, y la bandeja ocupa la pantalla completa con su propio layout, así
   * que el usuario elegía un chat de la lista y no ocurría nada visible. Peor que un error: parecía
   * que la lista estaba rota.
   *
   * Y aunque se hubiera visto, estaría mal: si ya estoy en la pantalla que existe para conversar, la
   * conversación tiene que abrirse ahí, entera, con su historial y su panel de cliente. Una ventanita
   * de 340 px encima de la bandeja es una versión peor de lo que ya está debajo.
   */
  /*
   * 🚨 ANTES DECÍA `/whatsapp` Y NUNCA ERA VERDADERO. El módulo se renombró a Chats y su ruta es
   * `/chats/inbox`; yo usé el nombre viejo sin verificarlo contra `routes.config`. El síntoma era
   * exactamente el que reportó el founder: dentro del módulo, elegir un chat de la lista seguía sin
   * abrir la conversación completa. El código estaba desplegado y no hacía nada — el peor tipo de bug,
   * porque parece un problema de despliegue.
   *
   * Se compara contra la ruta declarada, no contra un texto escrito a mano: si mañana se renombra otra
   * vez, esto sigue funcionando.
   */
  const rutaActual = useLocation().pathname;
  const enElModuloDeChats = rutaActual.includes(ROUTES.WHATSAPP_INBOX);

  const abrirChat = (conv: typeof conversaciones[number]) => {
    setAbierto(false);
    setFiltro('');

    /*
     * 🪤 LOS GRUPOS SIEMPRE VAN A LA BANDEJA. El dock encuentra la conversación por el teléfono del
     * cliente, y un grupo no tiene uno: su identificador es "g:...". Mandarlo al dock abriría una
     * ventana que nunca encuentra nada.
     */
    const irALaBandeja = enElModuloDeChats || (conv.contact_phone ?? '').startsWith('g:');

    if (irALaBandeja) {
      /*
       * 🪤 SE NAVEGA POR EL SLUG Y SE PRESERVA EL QUERY STRING, igual que hace la bandeja consigo
       * misma. El slug (`display_id`) es lo que la ruta espera; y sin el query string se pierde el
       * filtro del sidebar (?channel=, ?bucket=, ?tag=), así que el usuario abre el chat y de paso se
       * le reinicia la vista que tenía armada.
       */
      const slug = conversationSlug(conv.display_id) ?? conv.id;
      storeNavigate(ROUTES.WHATSAPP_CONVERSATION.replace(':conversationId', slug) + window.location.search);
      return;
    }

    dock.abrir({
      telefono: conv.contact_phone,
      nombre: waDisplayName(conv.contact_name, conv.contact_phone),
    });
  };

  const activador = (
    <button
      type="button"
      onClick={() => setAbierto((v) => !v)}
      aria-label={sinLeer > 0 ? `Chats (${sinLeer} sin leer)` : 'Chats'}
      /*
       * 🪤 `aria-expanded` NO ES SOLO ACCESIBILIDAD ACÁ: es de dónde sale el fondo gris que indica
       * qué panel está abierto. Antes ese fondo lo tenía SIEMPRE el ícono de wifi —Polaris se lo pone
       * a su activador— y nunca estos dos, así que el único indicador visual señalaba al menú que no
       * estaba abierto. Un estado que no corresponde al real enseña al usuario a ignorarlo.
       */
      aria-expanded={abierto}
      aria-haspopup="menu"
      className="sf-topbar-icon-button"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', width: 36, height: 36,
        background: 'transparent', border: 'none', borderRadius: 8,
        cursor: 'pointer', color: 'inherit',
      }}
    >
      <Icon source={ChatIcon} tone="base" />
      {sinLeer > 0 && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', top: 1, right: 1, minWidth: 15, height: 15,
            padding: '0 3px', borderRadius: 9, background: 'var(--p-color-bg-fill-critical)',
            color: '#fff', fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}
        >
          {sinLeer > 9 ? '9+' : sinLeer}
        </span>
      )}
    </button>
  );

  if (sinContexto) return null;

  return (
    <PanelDelEncabezado
      abierto={abierto}
      onCerrar={() => setAbierto(false)}
      activador={activador}
      titulo="Chats"
      cargando={nav?.conversationsLoading}
      pestanias={[
        { id: 'todos', texto: 'Todos' },
        { id: 'sin_leer', texto: 'No leídos', contador: sinLeer },
        // La pestaña de grupos solo aparece si el negocio tiene grupos: en la mayoría no existen,
        // y una pestaña que siempre da vacío es ruido.
        ...(grupos > 0 ? [{ id: 'grupos', texto: 'Grupos', contador: grupos }] : []),
      ]}
      pestaniaActiva={pestania}
      onPestania={(id) => setPestania(id as typeof pestania)}
      encabezadoExtra={(
        <TextField
          label="Buscar chats"
          labelHidden
          value={filtro}
          onChange={setFiltro}
          placeholder="Buscar por nombre o número"
          prefix={<Icon source={SearchIcon} tone="subdued" />}
          autoComplete="off"
        />
      )}
      vacio={(
        <Text as="p" tone="subdued">
          {filtro
            ? 'Ningún chat cargado coincide. Busca en Chats para revisar todos.'
            : pestania === 'sin_leer' ? 'No hay chats sin leer. Todo al día.'
            : pestania === 'grupos' ? 'No hay grupos en esta bandeja.'
            : 'Todavía no hay conversaciones.'}
        </Text>
      )}
      pie={{ texto: 'Ver todos los chats', onClick: () => { setAbierto(false); setFiltro(''); storeNavigate(ROUTES.WHATSAPP_INBOX); } }}
    >
      {visibles.map((c) => {
        const nombre = waDisplayName(c.contact_name, c.contact_phone);
        return (
          <FilaDelPanel
            key={c.id}
            onClick={() => abrirChat(c)}
            avatar={(
              <WaAvatar
                size={40}
                src={c.contact_photo_url}
                initials={getInitials(c.contact_name, c.contact_phone)}
                bg={getAvatarColor(c.contact_name)}
                fontSize={14}
                alt={nombre}
              />
            )}
            titulo={nombre}
            detalle={<VistaPreviaDelUltimo conv={c} />}
            hora={waTimeAgo(c.last_message_at)}
            sinLeer={c.unread_count}
            /*
             * 🪤 EL MENÚ VA DENTRO DE UN BORDE DE ERROR. Es una comodidad: si algún día vuelve a
             * lanzar —como pasó el 23-ago con un aviso que exigía estar dentro del Frame—, tiene
             * que desaparecer él solo y dejar la lista de chats funcionando. Tumbar la aplicación
             * entera para no poder archivar un chat es un intercambio que no cierra por ningún lado.
             */
            acciones={(
              <ErrorBoundarySection sectionName="Menú del chat" fallback={null}>
                <MenuDelChat conv={c} />
              </ErrorBoundarySection>
            )}
          />
        );
      })}
    </PanelDelEncabezado>
  );
}

/**
 * La vista previa del último mensaje, con la misma señal de dirección que el módulo de Chats.
 *
 * 🪤 SIN ESTO LOS DOS SENTIDOS SE VEÍAN IGUAL, que es el problema que reportó el founder: en la lista
 * del encabezado no había forma de saber si el último mensaje lo mandamos nosotros o lo mandó el
 * cliente. Y esa es justo la información que uno busca al mirar la lista de reojo: si el último es
 * del cliente, hay que contestar; si es nuestro, ya está atendido y el check dice si lo leyó.
 *
 * 🪤 DELIBERADAMENTE NO TRAE LAS ETIQUETAS NI EL ASIGNADO. El módulo de Chats sí los muestra —«venta
 * caliente», «interés: duromax», el nombre del asesor— y ahí corresponde: es la pantalla donde se
 * TRABAJA, con ancho completo y filtros. Este panel tiene 340 px y otra función: mirar de reojo si
 * hay algo pendiente. Sumarle las etiquetas obliga a leer tres renglones por fila para responder una
 * pregunta de un segundo, y empuja la hora y el globito de no leídos fuera de la vista. La regla que
 * siguen las apps que hacen esto bien es que el panel del encabezado resume y la pantalla completa
 * detalla; el panel dice CUÁNTOS y QUIÉN, y para el resto hay un «Ver todos los chats» abajo.
 */
function VistaPreviaDelUltimo({ conv }: { conv: WaConversation }) {
  const texto = waMediaPreview(conv.last_message_type, conv.last_message_body) || 'Sin mensajes';
  const esNuestro = conv.last_message_direction === 'outbound';

  if (!esNuestro) {
    // Entrante: sin checks. Los checks son del que ENVÍA, y mostrarlos en un mensaje del cliente
    // haría creer que confirmamos su lectura — que es al revés.
    return <>{texto}</>;
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
      <WaDeliveryTicks status={conv.last_message_status ?? 'sent'} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {texto}
      </span>
    </span>
  );
}


/**
 * El menú de cada chat en el desplegable del encabezado.
 *
 * 🪤 LAS OPCIONES Y LO QUE HACEN NO SE ESCRIBEN ACÁ. Vienen de `accionesDeConversacion`, el mismo
 * módulo que usa la bandeja. Copiarlas habría creado la segunda definición de «archivar un chat», y
 * dos copias de una regla siempre terminan diciendo cosas distintas — la que nadie mira es la que
 * miente.
 *
 * 🪤 EL CLICK NO PUEDE SUBIR A LA FILA. La fila entera abre el chat: sin frenar la propagación,
 * tocar el menú abriría la conversación y cerraría el panel en el mismo movimiento.
 */
function MenuDelChat({ conv }: { conv: WaConversation }) {
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const { tenantId, user } = useAuth();
  const nav = useWaInboxNavOptional();
  /*
   * 🪤 LA VERSIÓN OPCIONAL DEL AVISO, y esto tumbó la página. `useAdminToast` LANZA si no está
   * dentro de su proveedor —«must be used within AdminToastProvider (inside Frame)»— y el
   * encabezado se monta en pantallas cuyo marco no lo tiene. El menú de un chat es una comodidad:
   * no puede voltear la aplicación entera cuando se dibuja en el lugar equivocado.
   *
   * Es exactamente la misma trampa que `AccionesDeContacto` ya documentaba para el dock, dos
   * archivos más allá. La leí y la repetí igual.
   */
  const toast = useAdminToastOptional();
  const storeNavigate = useStoreNavigate();

  const ejecutar = async (accion: AccionDeConversacion) => {
    setAbierto(false);

    /*
     * Etiquetar y eliminar viven en la bandeja: la primera abre un modal y la segunda tiene
     * consecuencias que hay que poder mirar. Desde un panel de vistazo rápido, lo honesto es llevar
     * ahí en vez de ofrecer media versión.
     */
    if (accion === 'label' || accion === 'delete') {
      const slug = conversationSlug(conv.display_id) ?? conv.id;
      storeNavigate(ROUTES.WHATSAPP_CONVERSATION.replace(':conversationId', slug));
      return;
    }

    const pregunta = confirmacionNecesaria(accion, waDisplayName(conv.contact_name, conv.contact_phone));
    if (pregunta && !confirm(pregunta)) return;

    setOcupado(true);
    const { ok, error } = await persistirAccion(conv, accion, { tenantId, userId: user?.id });
    setOcupado(false);

    if (!ok) {
      toast?.error('No se pudo aplicar el cambio', error ?? 'Prueba de nuevo en un momento.');
      return;
    }
    // La lista del encabezado y la de la bandeja salen del MISMO contexto: se refresca una vez y
    // las dos quedan iguales. Sin esto, el encabezado mostraría el chat archivado hasta recargar.
    await nav?.loadConversations?.();
  };

  const { principales, peligrosas } = menuDeConversacion(conv, (accion) => void ejecutar(accion));

  return (
    <span
      onClick={(e) => { e.stopPropagation(); }}
      role="presentation"
      // Lo lee el CSS de la fila para no esconder la flecha mientras su menú está desplegado.
      data-menu-abierto={abierto ? 'true' : 'false'}
    >
      <Popover
        active={abierto}
        onClose={() => setAbierto(false)}
        preferredAlignment="right"
        // Por encima del panel que lo contiene: con la capa por defecto se abre DEBAJO y no se ve.
        zIndexOverride={Z_SOBRE_EL_PANEL}
        activator={(
          <button
            type="button"
            aria-label={`Opciones de ${waDisplayName(conv.contact_name, conv.contact_phone)}`}
            onClick={(e) => { e.stopPropagation(); setAbierto((v) => !v); }}
            disabled={ocupado}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: 6, border: 'none',
              background: 'transparent', cursor: ocupado ? 'default' : 'pointer',
              color: 'inherit', opacity: ocupado ? 0.5 : 1,
            }}
          >
            <Icon source={ChevronDownIcon} tone="subdued" />
          </button>
        )}
      >
        <ActionList actionRole="menuitem" sections={[{ items: principales }, { items: peligrosas }]} />
      </Popover>
    </span>
  );
}
